import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { UserItem } from '@/types/userItem';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { toast } from '@/lib/toast';
import { useAuth } from '@/hooks/useAuth';
import { MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WANTLIST_CATEGORY_LABELS } from '@/types/listing';

interface QuickListDialogProps {
  item: UserItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function QuickListDialog({ item, open, onOpenChange, onSuccess }: QuickListDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Profile data
  const [profileLocation, setProfileLocation] = useState<{ city: string; stateCode: string; lat?: number; lng?: number } | null>(null);
  const [profileWillShip, setProfileWillShip] = useState(false);
  const [profileAcceptsTrades, setProfileAcceptsTrades] = useState(false);
  
  // Form state
  const [askingPrice, setAskingPrice] = useState('');
  const [isNegotiable, setIsNegotiable] = useState(false);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('city, state_code, latitude, longitude, will_ship_items, accepts_trades')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (data?.city && data?.state_code) {
        setProfileLocation({
          city: data.city,
          stateCode: data.state_code,
          lat: data.latitude ? Number(data.latitude) : undefined,
          lng: data.longitude ? Number(data.longitude) : undefined,
        });
      } else {
        setProfileLocation(null);
      }
      setProfileWillShip(data?.will_ship_items ?? false);
      setProfileAcceptsTrades(data?.accepts_trades ?? false);
    };
    if (open) {
      fetchProfile();
    }
  }, [user, open]);

  // Reset form when item changes or dialog opens
  useEffect(() => {
    if (item && open) {
      // Pre-fill asking price from existing asking_price or target_price
      const priceInDollars = item.asking_price_cents 
        ? (item.asking_price_cents / 100).toFixed(2)
        : item.target_price_cents 
          ? (item.target_price_cents / 100).toFixed(2)
          : '';
      setAskingPrice(priceInDollars);
      setIsNegotiable(item.is_negotiable ?? false);
    }
  }, [item, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item) return;

    const priceCents = askingPrice ? Math.round(parseFloat(askingPrice) * 100) : null;
    
    // Price required unless open to offers
    if (!priceCents && !isNegotiable) {
      toast.error('Please enter a price or enable "Open to Offers"');
      return;
    }

    // Profile location required
    if (!profileLocation) {
      toast.error('Set your location in Profile to list items');
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData: Record<string, unknown> = {
        visibility: 'listed',
        is_listed: true,
        asking_price_cents: priceCents,
        is_negotiable: isNegotiable,
        accepts_trades: profileAcceptsTrades,
        ships_anywhere: profileWillShip,
        city: profileLocation.city,
        state_code: profileLocation.stateCode,
        // latitude/longitude removed from user_items for privacy

      };

      const { error } = await supabase
        .from('user_items')
        .update(updateData as any)
        .eq('id', item.id);

      if (error) throw error;

      toast.success('Item listed for sale');
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error listing item:', error);
      toast.error('Failed to list item');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!item) return null;

  const firstImage = item.images?.[0]?.image_url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>List Item for Sale</DialogTitle>
          <DialogDescription>
            Set your asking price to list this item on the marketplace.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Item preview */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-black/20 border border-white/[0.08]">
            {firstImage ? (
              <img 
                src={firstImage} 
                alt={item.title} 
                className="w-12 h-12 rounded-md object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-md bg-black/30 flex items-center justify-center text-white/40 text-xs">
                No img
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{item.title}</p>
              <p className="text-sm text-white/60">{WANTLIST_CATEGORY_LABELS[item.category] || item.category}</p>
            </div>
          </div>

          {/* Profile location check */}
          {!profileLocation && (
            <div className="p-3 rounded-lg bg-warning/10 border border-warning/30 text-warning text-sm">
              <p className="font-medium">Location required</p>
              <p className="text-warning/80">
                <Link to="/settings" className="underline hover:text-warning">
                  Set your location in Profile
                </Link>{' '}
                to list items.
              </p>
            </div>
          )}

          {profileLocation && (
            <p className="text-sm text-white/60 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {profileLocation.city}, {profileLocation.stateCode}
            </p>
          )}

          {/* Asking Price */}
          <div className="space-y-2">
            <Label htmlFor="asking-price">
              Asking Price {!isNegotiable && <span className="text-destructive">*</span>}
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">$</span>
              <Input
                id="asking-price"
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                {...PRICE_INPUT_PROPS}
                placeholder="0.00"
                value={askingPrice}
                onChange={(e) => setAskingPrice(e.target.value.replace(/[^0-9.]/g, ''))}
                className="pl-7"

              />
            </div>
            {item.target_price_cents && !item.asking_price_cents && (
              <p className="text-xs text-white/50">
                Pre-filled from your price goal
              </p>
            )}
          </div>

          {/* Open to Offers */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="is-negotiable"
              checked={isNegotiable}
              onCheckedChange={(checked) => setIsNegotiable(checked === true)}
            />
            <Label htmlFor="is-negotiable" className="cursor-pointer text-sm">
              Open to offers
            </Label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting || (!askingPrice && !isNegotiable) || !profileLocation}
            >
              {isSubmitting ? 'Listing...' : 'List for Sale'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
